import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { throttle } from 'lodash'

const DEFAULT_THROTTLE_MS = 180000 // 3 minutes

/**
 * Throttles a callback: fires immediately, then blocks for `throttleMs`.
 * Unlike debounce, continued clicking cannot extend the blocked window.
 *
 * `isThrottled` mirrors the blocked window so callers can disable their trigger.
 */
export const useThrottledAction = (
  callback: () => void,
  throttleMs: number = DEFAULT_THROTTLE_MS
) => {
  const [isThrottled, setIsThrottled] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const callbackRef = useRef(callback)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  const throttledFn = useMemo(
    () =>
      throttle(
        () => {
          callbackRef.current()
          setIsThrottled(true)
          timeoutRef.current = setTimeout(
            () => setIsThrottled(false),
            throttleMs
          )
        },
        throttleMs,
        { leading: true, trailing: false }
      ),
    [throttleMs]
  )

  const trigger = useCallback(() => {
    throttledFn()
  }, [throttledFn])

  useEffect(() => {
    return () => {
      throttledFn.cancel()
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [throttledFn])

  return { trigger, isThrottled }
}
